/**
 * Cuánto del mes está conciliado, contado en COSAS y no en euros.
 *
 * Una cosa es cualquier pieza que hay que cuadrar: cada renta que tocaba cobrar, cada gasto
 * (con su ticket y revisado), cada gasto fijo que debía llegar y cada movimiento del extracto.
 * El porcentaje es cuántas de esas piezas están ya en su sitio. En euros mentiría: una comunidad
 * de 900 € sin ticket pesaría treinta veces más que una factura de luz sin ticket, y las dos
 * faltan igual.
 *
 * Sin extracto, el propio extracto cuenta como una cosa pendiente: un mes sin banco no puede
 * salir al 100 % aunque estén todos los tickets.
 */
import type { BankMovement, LedgerEntry, LedgerMonthResponse, LedgerProperty } from '@naar/shared';
import { GASTOS_GENERALES_ID, GASTOS_GENERALES_NOMBRE } from '@naar/shared';

/**
 * Los GASTOS GENERALES (de ningún inmueble) como un «inmueble» más de la pantalla: así salen en
 * la conciliación, en «Lo que falta», en su tarjeta para soltar tickets y en los desplegables,
 * sin que cada sitio tenga que saber que existen. En la API son apuntes sin inmueble.
 */
export function generalComoInmueble(data: LedgerMonthResponse): LedgerProperty {
  // `general` llega desde la API nueva; con una API anterior, vacío.
  const general = data.general ?? { expenses: '0.00', entries: [], gaps: [] };
  return {
    id: GASTOS_GENERALES_ID,
    name: GASTOS_GENERALES_NOMBRE,
    city: '',
    portfolioName: '',
    income: '0.00',
    expectedIncome: '0.00',
    expenses: general.expenses,
    net: (-Number(general.expenses)).toFixed(2),
    entries: general.entries,
    gaps: general.gaps,
    payments: [],
  };
}

/** Los inmuebles del mes y, al final, los gastos generales. */
export function conGenerales(data: LedgerMonthResponse): LedgerProperty[] {
  return [...data.properties, generalComoInmueble(data)];
}

/** El inmueble elegido en pantalla, como lo quiere la API: `general` es «ninguno». */
export function aPropertyId(id: string): string | null {
  return id === GASTOS_GENERALES_ID ? null : id;
}

export interface Bloque {
  hechas: number;
  total: number;
}

export interface MetricasMes {
  cobros: Bloque & { sinCobrar: number };
  gastos: Bloque & { sinTicket: number; sinRevisar: number; noHaLlegado: number; apuntes: number };
  banco: Bloque & {
    pendientes: number;
    sinExtracto: boolean;
    ingresosSinIdentificar: number;
    cargosSinTicket: number;
  };
  /** De 0 a 100, o `null` si el mes no tiene nada que cuadrar todavía. */
  porcentaje: number | null;
  pendientes: number;
}

type Motivo = LedgerProperty['gaps'][number]['reason'];

function cuenta(properties: LedgerProperty[], reason: Motivo): number {
  return properties.reduce(
    (sum, property) => sum + property.gaps.filter((gap) => gap.reason === reason).length,
    0,
  );
}

export function metricasDelMes(data: LedgerMonthResponse): MetricasMes {
  // Los gastos generales cuentan como los de cualquier inmueble: también les puede faltar el ticket.
  const properties = conGenerales(data);

  const cobrados = properties.reduce((sum, property) => sum + property.payments.length, 0);
  const sinCobrar = cuenta(properties, 'sin_cobrar');

  const entries = properties.flatMap((property) => property.entries);
  const gastosOk = entries.filter((entry) => entry.document && entry.status === 'confirmado').length;
  const noHaLlegado = cuenta(properties, 'sin_apunte');

  const movements = data.bank.movements;
  const pendientes = movements.filter((movement) => movement.status === 'pendiente');
  const sinExtracto = data.bank.statements.length === 0 && movements.length === 0;

  const cobros = { hechas: cobrados, total: cobrados + sinCobrar, sinCobrar };
  const gastos = {
    hechas: gastosOk,
    total: entries.length + noHaLlegado,
    sinTicket: cuenta(properties, 'sin_factura'),
    sinRevisar: cuenta(properties, 'sin_confirmar'),
    noHaLlegado,
    apuntes: entries.length,
  };
  const banco = {
    hechas: movements.length - pendientes.length,
    // El extracto que falta cuenta como una cosa por hacer.
    total: movements.length + (sinExtracto ? 1 : 0),
    pendientes: pendientes.length,
    sinExtracto,
    ingresosSinIdentificar: pendientes.filter((movement) => movement.kind === 'ingreso').length,
    cargosSinTicket: pendientes.filter((movement) => movement.kind === 'gasto').length,
  };

  const total = cobros.total + gastos.total + banco.total;
  const hechas = cobros.hechas + gastos.hechas + banco.hechas;

  return {
    cobros,
    gastos,
    banco,
    porcentaje: total === 0 ? null : Math.floor((hechas / total) * 100),
    pendientes: total - hechas,
  };
}

/** Lo que tiene un inmueble por cuadrar y cuántas de sus piezas ya están, para su tarjeta. */
export function metricasDelInmueble(property: LedgerProperty): Bloque {
  const gastosOk = property.entries.filter((entry) => entry.document && entry.status === 'confirmado').length;
  const noHaLlegado = property.gaps.filter((gap) => gap.reason === 'sin_apunte').length;
  const sinCobrar = property.gaps.filter((gap) => gap.reason === 'sin_cobrar').length;
  return {
    hechas: gastosOk + property.payments.length,
    total: property.entries.length + noHaLlegado + property.payments.length + sinCobrar,
  };
}

/** La fracción de un bloque, para su barra. Un bloque vacío no está «al 0 %»: no tiene nada. */
export function fraccion(bloque: Bloque): number {
  return bloque.total === 0 ? 1 : bloque.hechas / bloque.total;
}

// ---------------------------------------------------------------------------
// Filas de la tabla de conciliación
// ---------------------------------------------------------------------------

/**
 * El estado de una fila, en las tres palabras que se usan al cuadrar:
 *
 *  · COMPLETO — cuadra con el banco y tiene su papel (el ticket, el justificante; para un cobro
 *    identificado en el extracto, el propio extracto es el papel).
 *  · COMPLETO SIN JUSTIFICAR — está en el banco y se sabe qué es, pero le falta el papel.
 *  · PENDIENTE — falta algo para darlo por bueno: el cargo en el banco, revisarlo, saber de
 *    quién es, o directamente que llegue.
 */
export type EstadoFila = 'completo' | 'sin_justificar' | 'pendiente';

export interface FilaConciliacion {
  key: string;
  fecha: string | null;
  concepto: string;
  /** Con signo: positivo entra, negativo sale. */
  importe: number | null;
  /** Saldo de la cuenta tras este movimiento. Solo en las filas que pasan por el banco. */
  saldo: number | null;
  estado: EstadoFila;
  proveedor: string | null;
  inmueble: { id: string; name: string } | null;
  /** El papel que lo respalda, o `extracto` cuando el propio extracto lo justifica. */
  soporte: { id: string; name: string } | 'extracto' | null;
  observaciones: string | null;
  /** Lo que todavía no ha pasado (un gasto fijo que no llegó, una renta sin cobrar). */
  prevista: boolean;
  /** La duda que dejó la IA en un movimiento pendiente: se enseña como «Duda: …». */
  duda: string | null;
  /** Por qué lo decidió la IA, cuando lo resolvió ella. `null` si no lo tocó. */
  porLaIa: string | null;
  /** Para abrir lo que corresponde al pulsar la fila. */
  entry?: { entry: LedgerEntry; propertyName: string };
  movement?: BankMovement;
  /** Un cobro registrado fuera del banco: su justificante y sus observaciones se corrigen aquí. */
  payment?: { id: string; reference: string | null };
  /**
   * QUIÉN PAGA un ingreso: el inquilino al que está imputado o, si nadie lo ha dicho todavía, el
   * que propone la IA (o el más probable de las sugerencias) con `sugerido: true`. Lo sugerido
   * se enseña y viene elegido al editarlo, pero no está guardado. Sin valor en los gastos.
   */
  pagador?: Pagador | null;
}

export interface Pagador {
  id: string;
  name: string;
  sugerido: boolean;
  /** Por qué se sugiere, cuando es una sugerencia. */
  motivo: string | null;
}

/** Quién paga un movimiento del banco: lo guardado o, en uno pendiente, lo que se propone. */
export function pagadorDe(movement: BankMovement, bank: LedgerMonthResponse['bank']): Pagador | null {
  if (movement.kind !== 'ingreso') return null;
  if (movement.tenant) return { id: movement.tenant.id, name: movement.tenant.fullName, sugerido: false, motivo: null };
  if (movement.status !== 'pendiente') return null;
  const nombre = (id: string) => bank.tenantOptions.find((option) => option.id === id)?.fullName ?? null;
  const ia = movement.aiProposal;
  if (ia?.action === 'tenant' && ia.tenantId) {
    const name = nombre(ia.tenantId);
    if (name) return { id: ia.tenantId, name, sugerido: true, motivo: `La IA lo propone: ${ia.reason}` };
  }
  const primera = movement.suggestions.tenants[0];
  const name = primera ? nombre(primera.id) : null;
  return primera && name ? { id: primera.id, name, sugerido: true, motivo: primera.reason } : null;
}

export interface TablaConciliacion {
  filas: FilaConciliacion[];
  /** `true` si el saldo parte del saldo final de un extracto; si no, es el acumulado del mes. */
  saldoReal: boolean;
}

const METODO: Record<LedgerProperty['payments'][number]['method'], string> = {
  banco: 'banco',
  efectivo: 'en efectivo',
  bizum: 'por Bizum',
  transferencia: 'desde otra cuenta',
  tarjeta: 'con tarjeta',
  domiciliacion: 'domiciliado',
};

export function filasDelMes(data: LedgerMonthResponse): TablaConciliacion {
  const porEntry = new Map<string, { entry: LedgerEntry; property: LedgerProperty }>();
  const inmuebles = conGenerales(data);
  for (const property of inmuebles) {
    for (const entry of property.entries) porEntry.set(entry.id, { entry, property });
  }
  const usados = new Set<string>();
  const filas: Omit<FilaConciliacion, 'saldo'>[] = [];

  // 1. Lo que pasó por el banco.
  for (const movement of data.bank.movements) {
    const importe = Number(movement.amount);
    const ingreso = movement.kind === 'ingreso';
    const ref = movement.expenseEntry ? porEntry.get(movement.expenseEntry.id) : undefined;
    if (ref) usados.add(ref.entry.id);
    const porLaIa = movement.matchMethod === 'ia' ? movement.matchDetail ?? 'Lo decidió la IA.' : null;
    const base = {
      key: `m:${movement.id}`,
      fecha: movement.bookedAt,
      importe,
      prevista: false,
      movement,
      duda: movement.status === 'pendiente' ? movement.aiDoubt : null,
      porLaIa,
      pagador: pagadorDe(movement, data.bank),
    };

    if (movement.status === 'pendiente') {
      filas.push({
        ...base,
        concepto: movement.description,
        estado: 'pendiente',
        proveedor: movement.vendorName,
        inmueble: movement.property,
        soporte: movement.document,
        // Con duda de la IA, la duda ES la observación (se pinta aparte, con su etiqueta).
        observaciones:
          movement.notes ?? (movement.aiDoubt ? null : ingreso ? 'Ingreso sin identificar' : 'Cargo sin explicar'),
      });
    } else if (!ingreso && (movement.expenseEntry || ref)) {
      const entry = ref?.entry;
      filas.push({
        ...base,
        concepto: entry?.concept ?? movement.expenseEntry?.concept ?? movement.description,
        estado: entry?.document ? (entry.status === 'confirmado' ? 'completo' : 'pendiente') : 'sin_justificar',
        proveedor: entry?.vendorName ?? movement.vendorName ?? movement.description,
        inmueble: ref
          ? { id: ref.property.id, name: ref.property.name }
          : movement.expenseEntry
            ? { id: '', name: movement.expenseEntry.propertyName }
            : null,
        soporte: entry?.document
          ? { id: entry.document.id, name: entry.document.name }
          : ref
            ? null
            : movement.document,
        observaciones:
          entry?.notes ??
          movement.notes ??
          (entry && !entry.document ? 'Falta el ticket' : entry?.status === 'borrador' ? 'Sin revisar' : null),
        ...(ref ? { entry: { entry: ref.entry, propertyName: ref.property.name } } : {}),
      });
    } else if (ingreso && movement.status !== 'ignorado') {
      filas.push({
        ...base,
        concepto: movement.tenant ? `Renta · ${movement.tenant.fullName}` : movement.description,
        estado: 'completo',
        proveedor: movement.vendorName ?? movement.tenant?.fullName ?? movement.description,
        inmueble: movement.property,
        soporte: movement.document ?? 'extracto',
        observaciones: movement.notes ?? movement.matchDetail,
      });
    } else {
      // Un cargo imputado sin apunte, o un movimiento ignorado (un traspaso, una comisión).
      filas.push({
        ...base,
        concepto: movement.description,
        estado: movement.document ? 'completo' : 'sin_justificar',
        proveedor: movement.vendorName,
        inmueble: movement.property,
        soporte: movement.document,
        observaciones:
          movement.notes ??
          (movement.status === 'ignorado' ? porLaIa ?? 'Ignorado al conciliar' : 'Sin ticket'),
      });
    }
  }

  for (const property of inmuebles) {
    const inmueble = { id: property.id, name: property.name };

    // 2. Gastos apuntados que no han casado con ningún cargo del banco.
    for (const entry of property.entries) {
      if (usados.has(entry.id)) continue;
      filas.push({
        key: `e:${entry.id}`,
        fecha: entry.issuedAt,
        concepto: entry.concept,
        importe: -Number(entry.amount),
        estado: 'pendiente',
        proveedor: entry.vendorName,
        inmueble,
        soporte: entry.document ? { id: entry.document.id, name: entry.document.name } : null,
        observaciones:
          entry.notes ??
          (entry.status === 'borrador'
            ? 'Sin revisar'
            : entry.document
              ? 'Falta el cargo en el banco'
              : 'Sin ticket ni cargo en el banco'),
        prevista: false,
        duda: null,
        porLaIa: null,
        entry: { entry, propertyName: property.name },
      });
    }

    // 3. Cobros registrados fuera del banco.
    for (const payment of property.payments) {
      if (payment.movementId || !payment.paymentId) continue;
      filas.push({
        key: `p:${payment.paymentId}`,
        fecha: payment.bookedAt,
        concepto: `Renta · ${payment.tenantName ?? 'sin inquilino'}`,
        importe: Number(payment.amount),
        estado: payment.document ? 'completo' : 'sin_justificar',
        proveedor: payment.tenantName,
        inmueble,
        soporte: payment.document,
        observaciones: payment.reference ?? `Cobrado ${METODO[payment.method]}`,
        prevista: false,
        duda: null,
        porLaIa: null,
        payment: { id: payment.paymentId, reference: payment.reference },
        pagador: payment.tenantName ? { id: '', name: payment.tenantName, sugerido: false, motivo: null } : null,
      });
    }

    // 4. Lo que se esperaba y no ha pasado: gastos fijos sin llegar y rentas sin cobrar.
    for (const gap of property.gaps) {
      if (gap.reason !== 'sin_apunte' && gap.reason !== 'sin_cobrar') continue;
      const cobro = gap.reason === 'sin_cobrar';
      const esperado = gap.expectedAmount ? Number(gap.expectedAmount) : null;
      filas.push({
        key: `g:${gap.reason}:${gap.recurringExpenseId ?? gap.rentChargeId ?? gap.concept}`,
        fecha: null,
        concepto: gap.concept,
        importe: esperado === null ? null : cobro ? esperado : -esperado,
        estado: 'pendiente',
        proveedor: null,
        inmueble,
        soporte: null,
        observaciones: cobro ? 'Renta sin cobrar' : 'Gasto fijo que no ha llegado',
        prevista: true,
        duda: null,
        porLaIa: null,
      });
    }
  }

  filas.sort(
    (a, b) =>
      (a.fecha ?? '9999-99-99').localeCompare(b.fecha ?? '9999-99-99') || a.key.localeCompare(b.key),
  );

  /*
   * EL SALDO ES EL DE LA CUENTA, y solo lo mueven las filas que pasan por el banco: un ticket
   * que aún no se ha cargado no ha sacado dinero de ningún sitio. Si algún extracto trae su
   * saldo final, se parte del saldo inicial del mes que calcula la API (el extracto puede
   * acabar ya en el mes siguiente); si no, es el acumulado del mes. En céntimos, para que la
   * suma de floats no se desvíe fila a fila.
   */
  const inicial = data.bank.openingBalance;
  let centimos = inicial === null ? 0 : Math.round(Number(inicial) * 100);

  return {
    saldoReal: inicial !== null,
    filas: filas.map((fila) => {
      if (!fila.movement) return { ...fila, saldo: null };
      centimos += Math.round((fila.importe ?? 0) * 100);
      return { ...fila, saldo: centimos / 100 };
    }),
  };
}
